import { createConnectQueryKey } from '@connectrpc/connect-query'
import {
  deleteWorkflowCase,
  getWorkflowCase,
  listWorkflowCases,
  listWorkflowTagPool,
} from '@ebalance/proto/bffuser/v1/workflow_case_service-WorkflowCaseService_connectquery'
import { type QueryKey, useQueryClient } from '@tanstack/react-query'

import {
  type InvalidatingMutationResultFor,
  useInvalidatingMutation,
} from '~/hooks/useInvalidatingMutation'

/**
 * ワークフローケースを不可逆に (論理) 削除する mutation hook (spec AC-003f/g、
 * workflow-delete-transition design)。再開する手段は無い。
 *
 * WorkflowCases (一覧) と WorkflowCaseDetail (詳細) の両画面から使うため
 * `~/hooks/workflowCase/` (page-横断 hook) に置く (= useGetWorkflowCase と同様)。
 * caseId が undefined の一覧文脈では detail cache key を持たないため、
 * list query のみ invalidate すれば十分 (detail invalidate は呼び出し側が
 * 必要な時だけ渡す)。
 */
export function useDeleteWorkflowCase(
  caseId?: string,
): InvalidatingMutationResultFor<typeof deleteWorkflowCase> {
  return useInvalidatingMutation(
    deleteWorkflowCase,
    deleteWorkflowCaseAffectedQueryKeys(caseId),
  )
}

/**
 * 削除を受け付けなかった時 (先に更新・完了・削除されていた時、 spec AC-WFW-003s)
 * に、 削除の成功時と同じ query を読み直す関数を返す。
 */
export function useReloadAfterDeleteWorkflowCase(
  caseId?: string,
): () => Promise<void> {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all(
      deleteWorkflowCaseAffectedQueryKeys(caseId).map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    )
  }
}

function deleteWorkflowCaseAffectedQueryKeys(caseId?: string): QueryKey[] {
  return [
    createConnectQueryKey({ schema: listWorkflowCases, cardinality: 'finite' }),
    // 削除は org 全体のタグプール (AC-TAG-005) を縮小しうる (AC-TAG-007: 参照 0 件で
    // プールから消滅) ため、他画面のサジェスト・20 個上限チェックを最新化する。
    createConnectQueryKey({
      schema: listWorkflowTagPool,
      cardinality: 'finite',
    }),
    ...(caseId !== undefined
      ? [
          createConnectQueryKey({
            schema: getWorkflowCase,
            cardinality: 'finite',
            input: { id: caseId },
          }),
        ]
      : []),
  ]
}
